import { styled } from "@mui/material/styles";
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  Button, 
  Box, 
  Typography,
  Select,
  Divider,
} from "@mui/material";

export const BoardDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    backgroundColor: '#282E3E',
    color: 'white',
    width: '400px',
    maxWidth: '95vw',
    borderRadius: '10px',
    boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.5)',
  }
}));

export const BoardDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: '#282E3E',
  color: 'white',
  padding: theme.spacing(1.5, 2),
  fontSize: '1rem',
  textAlign: 'center',
  position: 'relative',
  paddingBottom: theme.spacing(1)
}));

export const BoardDialogContent = styled(DialogContent)(({ theme }) => ({
  backgroundColor: '#282E3E',
  color: 'white',
  padding: theme.spacing(2),
}));

export const BoardDialogActions = styled(DialogActions)(({ theme }) => ({
  backgroundColor: '#282E3E',
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

export const BoardPreviewSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#1E2233',
  borderRadius: '4px',
  padding: theme.spacing(1),
  marginBottom: theme.spacing(2),
  textAlign: 'center',
}));

export const BoardPreviewImage = styled('img')({
  width: '100%',
  height: 'auto',
  borderRadius: '4px',
  marginBottom: '8px',
});

export const BackgroundOptionsList = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

export const BackgroundOption = styled(Box, {
  shouldForwardProp: (prop) => prop !== "selected"
})<{ selected?: boolean }>(({ theme, selected }) => ({
  flex: 1,
  height: '40px',
  borderRadius: '4px',
  overflow: 'hidden',
  cursor: 'pointer',
  border: selected ? '2px solid white' : '1px solid rgba(255, 255, 255, 0.2)',
  transition: 'all 0.2s',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
  },
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));

export const MoreOptionsButton = styled(Box)(({ theme }) => ({
  flex: 1,
  height: '40px',
  borderRadius: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
  },
}));

export const SectionLabel = styled(Typography)(({ theme }) => ({
  color: 'white',
  fontSize: '0.9rem',
  fontWeight: 500,
  marginBottom: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
}));

export const RequiredMark = styled('span')({
  color: '#F08C00',
  marginLeft: '2px',
});

export const BoardTitleField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== "isError",
})<{ isError?: boolean }>(({ theme, isError }) => ({
  marginBottom: theme.spacing(1),
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'transparent',
    '& fieldset': {
      borderColor: isError ? '#a30000' : 'rgba(255, 255, 255, 0.23)',
      borderWidth: isError ? '2px' : '1px',
    },
    '&:hover fieldset': {
      borderColor: isError ? '#a30000' : 'rgba(255, 255, 255, 0.38)',
    },
    '&.Mui-focused fieldset': {
      borderColor: isError ? '#a30000' : '#1976d2',
    },
  },
  '& .MuiInputBase-input': {
    color: 'white',
    padding: '10px 14px',
  },
}));

export const WarningText = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: '#F08C00',
  fontSize: '0.75rem',
  marginBottom: theme.spacing(2),
  '& svg': {
    fontSize: '16px',
    marginRight: theme.spacing(0.5),
  },
}));

export const VisibilitySelect = styled(Select)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: 'white',
  marginBottom: theme.spacing(2),
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.23)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.23)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.23)',
  },
  '& .MuiSvgIcon-root': {
    color: 'white',
  },
}));

export const InfoText = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.7)',
  fontSize: '0.75rem',
  marginBottom: theme.spacing(2),
}));

export const PromotionButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#3F1D60',
  color: 'white',
  textTransform: 'none',
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1, 2),
  '&:hover': {
    backgroundColor: '#4F2A70',
  },
  '& .MuiButton-startIcon': {
    marginRight: theme.spacing(1),
  },
}));

export const CreateButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "isValid",
})<{ isValid: boolean }>(({ theme, isValid }) => ({
  backgroundColor: isValid ? '#2A5885' : 'rgba(42, 88, 133, 0.5)',
  color: 'white',
  textTransform: 'none',
  padding: theme.spacing(1, 2),
  '&:hover': {
    backgroundColor: isValid ? '#3A6895' : 'rgba(42, 88, 133, 0.5)',
  },
  pointerEvents: isValid ? 'auto' : 'none',
}));

export const TemplateButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: 'white',
  textTransform: 'none',
  border: '1px solid rgba(255, 255, 255, 0.23)',
  padding: theme.spacing(1, 2),
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  },
}));

export const LegalText = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.5)',
  fontSize: '0.7rem',
  textAlign: 'center',
  marginTop: theme.spacing(1),
}));


export const DividerWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  padding: theme.spacing(1, 0),
  backgroundColor: '#282E3E',
}));

export const CustomDivider = styled(Divider)(({ theme }) => ({
  width: '90%',
  backgroundColor: '#282E3E',
}));